import { Injectable, Inject } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subject, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, filter, tap } from 'rxjs/operators';
import { AbstractLoginService } from '../../declaration/abstract-member-service';
import { MEMBER_LOGIN_WAY } from '../../entity/member-login-way';
import { LoginResultCode } from '../../declaration/LoginResultCode.enum';
import { GeneratorEnv, GENERATOR_ENV_TOKEN } from 'src/app/web-generator/generator-env-token';
import { GlobalComponentService } from '@techmore/global';
import { CommonHttpClientService } from 'src/app/share/service/common-http-client.service';

/** FB 登入服務 */
@Injectable()
export class FacebookService extends AbstractLoginService  {
  protected _isRefreshing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** FB client id 設定通知器 */
  private clientId$ = new BehaviorSubject(null);
  /** FB client id 設定通知器 */
  getClientId$() { return this.clientId$.asObservable(); }
  /** FB client id */
  get clientId() { return this.clientId$.value; }

  constructor(
    @Inject(GENERATOR_ENV_TOKEN) protected generatorEnv: GeneratorEnv,
    protected http: CommonHttpClientService,
    protected globalComponentService: GlobalComponentService,
  ) {
    super(generatorEnv);
  }

  init(clientId: string) {
    if (clientId) {
      if (clientId !== this.clientId$.value) {
        this.clientId$.next(clientId);
      }

      if (!document.getElementById('fb_api')) {
        const script = document.createElement('script');
        script.id = 'fb_api';
        script.src = 'https://connect.facebook.net/en_US/sdk.js';
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        document.head.appendChild(script);

        const that = this;
        (window as any).fbAsyncInit = function() {
          FB.init({
            appId: clientId,
            cookie: true,                     // Enable cookies to allow the server to access the session.
            xfbml: true,                     // Parse social plugins on this webpage.
            version: 'v11.0'                   // Use this Graph API version for this call.
          });

          // FB.getLoginStatus(function(response) {   // Called after the JS SDK has been initialized.
          //   statusChangeCallback(response);        // Returns the login status.
          // });

          that.loadMemberInfoFromStorage();
          that.initFinish$.next(true);
        };
      }
    } else {
      localStorage.removeItem('auth_code');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_refresh_token');
      localStorage.removeItem('auth_way');
    }
  }

  /** 將 sessionStorage 裡已有的auth_token載入到服務裡 */
  loadMemberInfoFromStorage() {
    let data: any;
    data = localStorage.getItem('auth_token');
    if (localStorage.getItem('auth_way') === 'facebook' && data) {
      try {
        const memberInfo = this.parseToken(data);
        if (JSON.stringify(memberInfo) !== JSON.stringify(this.memberInfo$.value)) {
          this.memberInfo$.next(memberInfo);
        }
      } catch (error) {
        console.error(error);
      }
    }
  }

  login(): Observable<any> {
    this.onLoginStart$.next();
    let redirectUrl = window.location.href;
    const ua = redirectUrl.split('/');
    redirectUrl = `${ua[0]}/${ua[1]}/${ua[2]}/${ua[3]}/memberFacebookLoading`;
    const state =  Math.random().toString(36).substring(0, 7);;

    const openedWindow = window.open(
    `https://www.facebook.com/dialog/oauth?client_id=${this.clientId}&scope=email&redirect_uri=${redirectUrl}&state=${state}&auth_type=reauthenticate`,
    '_blank',
    'height=500,resizable=yes,scrollbars=yes,width=700');

    // FIXME: 結果這一段好像沒用，fb的登入視窗轉導後彈出視窗似乎沒有opener，在本機會這樣，但其它正式環境又是正常的
    return new Observable(subscriber => {
      const checkWinClosedSubc = interval(1000).subscribe(() => {
        if (openedWindow.closed) {
          if (window['loginErrorReason']) {
            const error = window['loginErrorReason'];
            delete window['loginErrorReason'];
            this.onLoginEnd$.next(LoginResultCode.fail);
            checkWinClosedSubc.unsubscribe();
            subscriber.error(new Error(JSON.stringify(error)));
            subscriber.complete();
          } else {
            if (localStorage.getItem('auth_token')) {
              this.loadMemberInfoFromStorage();
              this.onLoginEnd$.next(LoginResultCode.success);
              subscriber.next(true);
            } else {
              this.onLoginEnd$.next(LoginResultCode.cancel);
              subscriber.next(false);
            }
            checkWinClosedSubc.unsubscribe();
            subscriber.complete();
          }
        }
      });
    });
  }

  logout(fromUser = true): Observable<any> {
    const that = this;

    return new Observable<any>(function subscribe(subscriber) {
      try {
        FB.getLoginStatus((response) => {   // See the onlogin handler
          if (response.status === 'connected') {   // Logged into your webpage and Facebook.
            FB.logout(function(response) {
              // Person is now logged out
              // checkLoginState();
              localStorage.removeItem('auth_code');
              localStorage.removeItem('auth_token');
              localStorage.removeItem('auth_refresh_token');
              localStorage.removeItem('auth_way');
              that.logout$.next();
              that.memberInfo$.next(undefined);
              subscriber.next(true);
            });
          } else {
            console.log('', response);
            subscriber.next(false);
            that.logout$.next();
            that.memberInfo$.next(undefined);
            localStorage.removeItem('auth_code');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_refresh_token');
            localStorage.removeItem('auth_way');
          }
        });
      } catch (error) {
        console.log(error);
        if (fromUser) {
          that.globalComponentService.openErrorAlert('您的 Facebook 帳號登出失敗，請確認第三方 cookies 未被封鎖再嘗試');
        } else {
          subscriber.next(false);
          that.logout$.next();
          that.memberInfo$.next(undefined);
          localStorage.removeItem('auth_code');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_refresh_token');
          localStorage.removeItem('auth_way');
        }
      }
    });
  }

  getMembertoken(authCode: string, redirectUrl: string): Observable<any> {
    const url = `${this.generatorEnv.apiHost}/api/member/oauth/token?grant_type=facebook&code=${authCode}&redirect_url=${redirectUrl}`;
    return this.http.post(url, JSON.stringify({}), {
      headers: {
        'access_token': localStorage.getItem('site_token')
      }
    }).pipe(
      catchError((error, observable) => this.errorHandler(error, observable)),
      tap(data => {
        localStorage.setItem('auth_token', data['accessToken']);
        localStorage.setItem('auth_refresh_token', data['refreshToken']);
        localStorage.setItem('auth_way', MEMBER_LOGIN_WAY.FACEBOOK);
        const memberInfo = this.parseToken(data['accessToken']);
        this.memberInfo$.next(memberInfo);
      })
    );
  }

  parseToken(authToken: string) {
    const base64Url = authToken.split('.')[1];
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    switch (base64.length % 4) {
      case 0: break;
      case 2: base64 += '=='; break;
      case 3: base64 += '='; break;
      default: break;
    }
    return JSON.parse(decodeURIComponent(escape(atob(base64))));
  }

  refreshMemberToken = (refreshToken: any): Observable<any> => {
    const url = `${this.generatorEnv.apiHost}/member/oauth/token?grant_type=auth_refresh_token&auth_refresh_token=${refreshToken}`;
    return this.http.post<any>(url, JSON.stringify({}));
  };

  errorHandler = (error: HttpErrorResponse, observable) => {
    if (error instanceof HttpErrorResponse) {
      switch (error.status) {
        case 611:
          let subject = new Subject<any>();

          if (!this._isRefreshing.getValue()) {
            this._isRefreshing.next(true);
            this.refreshMemberToken(localStorage.getItem('auth_refresh_token')).subscribe(data => {
              localStorage.setItem('auth_token', data['accessToken']);
              localStorage.setItem('auth_refresh_token', data['refreshToken']);
              this._isRefreshing.next(false);
            }, error1 => {
              this.globalComponentService.openSanckBar('會員已被登出，請重新登入', 'X', {duration: 5000, horizontalPosition: 'right'});
              localStorage.removeItem('auth_code');
              localStorage.removeItem('auth_token');
              localStorage.removeItem('auth_refresh_token');
              localStorage.removeItem('auth_way');
              this.logout$.next();
              this.memberInfo$.next(undefined);
            });
          }

          this._isRefreshing.pipe(
            filter(bool => !bool)
          ).subscribe((bool) => observable.subscribe(subject));

          return subject;
        default:
          break;
      }
    } else {
      // 未預期的程式錯誤
      console.error(error);
    }

    return throwError(error);
  }
}
